import { useEffect, useState } from "react";
import { Loader2, UserPlus, X } from "lucide-react";
import { listReviewers } from "./api";
import { ProjectDraft, Reviewer, VerityProject, reviewersOf } from "./types";

interface AddProjectModalProps {
  /** Present when editing; absent when adding. */
  project?: VerityProject | null;
  onCancel: () => void;
  onSubmit: (draft: ProjectDraft) => Promise<void>;
  /**
   * Which side the project is being made on. Sent on create; on an AWP project the party who owes
   * the work is the contractor, so the field is labelled that way. Same stored field either way.
   */
  reviewType?: "engineering" | "awp";
}

/**
 * The Add Project form.
 *
 * Four fields carry the table — name, project ID, supplier, status — and three more are optional
 * colour. Nothing is inferred: a project is created by a person who knows what it is, so there is no
 * reader step here the way there is for a standards document.
 */
const AddProjectModal = ({
  project,
  onCancel,
  onSubmit,
  reviewType = "engineering",
}: AddProjectModalProps) => {
  const awp = reviewType === "awp";
  const [name, setName] = useState(project?.name ?? "");
  const [projectRef, setProjectRef] = useState(project?.projectRef ?? "");
  const [supplier, setSupplier] = useState(project?.supplier ?? "");
  const [location, setLocation] = useState(project?.location ?? "");
  const [discipline, setDiscipline] = useState(project?.discipline ?? "");
  const [notes, setNotes] = useState(project?.notes ?? "");
  /**
   * AWP only: the project's construction work areas, which every package picks from.
   *
   * Set here by the administrator because they are fixed when a job is set up — the same moment the
   * project is created and its reviewers assigned. Existing rows keep their ids, so packages filed
   * under an area stay filed under it when the code or name is corrected.
   */
  const [areas, setAreas] = useState<{ id?: string; code: string; name: string; sequence: string }[]>(
    () =>
      project?.cwas?.length
        ? project.cwas.map((a) => ({
            id: a.id,
            code: a.code,
            name: a.name ?? "",
            sequence: a.sequence?.toString() ?? "",
          }))
        : [{ code: "", name: "", sequence: "" }],
  );
  const setArea = (i: number, patch: Partial<(typeof areas)[number]>) =>
    setAreas((prev) => prev.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Who is to do the review.
   *
   * Assigned HERE, at the moment a project is created, because that is when somebody knows who
   * should do it. It used to be a separate "Reviewers & Roles" screen, which is where reviewers are
   * listed but not where any of them get given work — so the assignment never happened there.
   *
   * Optional: a project can exist before it has an owner, and forcing a name at creation would only
   * teach people to pick whoever is first in the list.
   */
  const [picked, setPicked] = useState<string[]>(() =>
    reviewersOf(project ?? ({} as VerityProject)).map((r) => r.userId),
  );
  const [reviewers, setReviewers] = useState<Reviewer[] | null>(null);
  const [reviewerError, setReviewerError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const list = await listReviewers();
        if (live) setReviewers(list);
      } catch (err) {
        // A roster that will not load must not block creating a project — the assignment is the
        // one optional field on this form.
        if (live) {
          setReviewers([]);
          setReviewerError(err instanceof Error ? err.message : "Could not load the org roster.");
        }
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const ready = name.trim().length > 0 && supplier.trim().length > 0;

  const save = async () => {
    if (!ready || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        reviewType,
        // Blank rows are dropped server-side, so an untouched starter row adds nothing.
        ...(awp
          ? {
              cwas: areas.map((a) => ({
                id: a.id ?? "",
                code: a.code.trim(),
                name: a.name.trim() || undefined,
                sequence: a.sequence.trim() === "" ? undefined : Number(a.sequence),
              })),
              // The version this form was opened on. If somebody saved in between, the server
              // refuses rather than letting this copy of the work areas overwrite theirs.
              _etag: project?._etag,
            }
          : {}),
        name: name.trim(),
        projectRef: projectRef.trim(),
        supplier: supplier.trim(),
        location: location.trim() || undefined,
        discipline: discipline.trim() || undefined,
        notes: notes.trim() || undefined,
        // The name travels with each id so the record still reads properly after somebody leaves.
        // An empty list is sent as an empty list — that is how an assignment is taken away.
        reviewers: picked.map((userId) => ({
          userId,
          name: (reviewers ?? []).find((r) => r.userId === userId)?.name ?? userId,
        })),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the project.");
      setSaving(false);
    }
  };

  return (
    <div className="vr-backdrop" role="dialog" aria-modal="true" aria-label="Add project">
      <div className="vr-modal vr-modal-sm">
        <div className="vr-modal-head">
          <div>
            <h3>{project ? "Edit Project" : "Add Project"}</h3>
            <p>
              {awp
                ? "The project whose CWP, EWP and PWP work packages will be reviewed, and the contractor responsible for them."
                : "The piece of work deliverables are submitted against, and the supplier who owes them."}
            </p>
          </div>
          <button className="vr-rowbtn vr-icononly" onClick={onCancel} aria-label="Close">
            <X size={14} />
          </button>
        </div>

        <div className="vr-modal-body">
          <div className="vr-field">
            <label htmlFor="vr-proj-name">Project Name</label>
            <input
              id="vr-proj-name"
              className="vr-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Navajo Refining — Alkylation Unit"
              autoFocus
            />
          </div>

          {/* No status field. Status is derived from how far the project's deliverables have got —
              a dropdown here would record what somebody last remembered to set. */}
          <div className="vr-field-grid">
            <div className="vr-field">
              <label htmlFor="vr-proj-ref">Project ID</label>
              <input
                id="vr-proj-ref"
                className="vr-input"
                value={projectRef}
                onChange={(e) => setProjectRef(e.target.value)}
                placeholder="PRJ-2026-014"
              />
              <span className="vr-field-hint">The client's own reference, if there is one.</span>
            </div>

            <div className="vr-field">
              <label htmlFor="vr-proj-supplier">{awp ? "Contractor" : "Supplier"}</label>
              <input
                id="vr-proj-supplier"
                className="vr-input"
                value={supplier}
                onChange={(e) => setSupplier(e.target.value)}
                placeholder="Civil &amp; Structural Engineers Ltd"
              />
            </div>
          </div>

          <div className="vr-field-grid">
            <div className="vr-field">
              <label htmlFor="vr-proj-location">Location</label>
              <input
                id="vr-proj-location"
                className="vr-input"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Optional"
              />
            </div>

            {/* AWP: no project discipline — an AWP project spans every discipline, and each package
                carries its own. The engineering form is unchanged. */}
            {!awp && (
            <div className="vr-field">
              <label htmlFor="vr-proj-discipline">Discipline</label>
              <input
                id="vr-proj-discipline"
                className="vr-input"
                value={discipline}
                onChange={(e) => setDiscipline(e.target.value)}
                placeholder="Optional — Civil, Structural, Process…"
              />
            </div>
            )}
          </div>

          <div className="vr-field">
            <label htmlFor="vr-proj-reviewer">
              <UserPlus size={13} style={{ verticalAlign: "-2px", marginRight: ".35rem" }} />
              Assigned reviewer
            </label>
            {/* A LIST, NOT A DROPDOWN. A pack that needs a civil reviewer and a structural one is
                ordinary, and a single-select quietly made that impossible. Scrolls rather than
                growing, so a large org does not push the buttons off the dialog. */}
            <div className="vr-pick-list vr-reviewer-pick" id="vr-proj-reviewer">
              {reviewers === null && (
                <div className="vr-empty">
                  <Loader2 size={14} className="vr-spin-inline" /> Loading the org roster…
                </div>
              )}
              {reviewers?.length === 0 && (
                <div className="vr-empty">{reviewerError ?? "Nobody to assign."}</div>
              )}
              {(reviewers ?? []).map((r) => (
                <label key={r.userId} className="vr-pick-row">
                  <input
                    type="checkbox"
                    checked={picked.includes(r.userId)}
                    onChange={() =>
                      setPicked((prev) =>
                        prev.includes(r.userId)
                          ? prev.filter((id) => id !== r.userId)
                          : [...prev, r.userId],
                      )
                    }
                  />
                  <span className="vr-pick-body">
                    <span className="vr-pick-name">{r.name}</span>
                    <span className="vr-pick-meta">{r.email}</span>
                  </span>
                </label>
              ))}
            </div>
            <span className="vr-field-hint">
              {reviewerError && reviewers?.length
                ? reviewerError
                : picked.length === 0
                  ? "Unassigned — nobody will see this project on their own screen."
                  : `${picked.length} assigned. They submit the deliverables, parse them and run the compliance check; you follow the progress.`}
            </span>
          </div>

          {awp && (
            <div className="vr-field">
              <label>Construction work areas</label>
              <div className="vr-cwa-rows">
                {areas.map((a, i) => (
                  <div key={a.id ?? `new-${i}`} className="vr-cwa-row">
                    <input
                      className="vr-input"
                      value={a.code}
                      onChange={(e) => setArea(i, { code: e.target.value })}
                      placeholder="CWA-01"
                      aria-label="Work area code"
                    />
                    <input
                      className="vr-input"
                      value={a.name}
                      onChange={(e) => setArea(i, { name: e.target.value })}
                      placeholder="Process Area North"
                      aria-label="Work area name"
                    />
                    <input
                      className="vr-input"
                      type="number"
                      min={0}
                      value={a.sequence}
                      onChange={(e) => setArea(i, { sequence: e.target.value })}
                      placeholder="Seq"
                      aria-label="Build sequence"
                    />
                    <button
                      type="button"
                      className="vr-rowbtn vr-icononly vr-danger"
                      onClick={() => setAreas((prev) => prev.filter((_, j) => j !== i))}
                      aria-label="Remove work area"
                      title="Remove work area"
                    >
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="vr-rowbtn"
                style={{ alignSelf: "flex-start" }}
                onClick={() => setAreas((prev) => [...prev, { code: "", name: "", sequence: "" }])}
              >
                + Add work area
              </button>
              <span className="vr-field-hint">
                Code, name and build sequence. Reviewers pick from these on every package; the
                hierarchy lists them in sequence order.
              </span>
            </div>
          )}

          <div className="vr-field">
            <label htmlFor="vr-proj-notes">Notes</label>
            <input
              id="vr-proj-notes"
              className="vr-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional"
            />
          </div>

          {error && <div className="vr-form-error">{error}</div>}
        </div>

        <div className="vr-modal-foot">
          <button className="vr-btn vr-btn-ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button className="vr-btn vr-btn-glow" onClick={() => void save()} disabled={!ready || saving}>
            {saving ? (
              <>
                <Loader2 size={14} className="vr-spin-inline" /> Saving…
              </>
            ) : (
              <>{project ? "Save Changes" : "Add Project"}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddProjectModal;
